import React, { useEffect, useRef, useState } from "react";
import { mountStatistics } from "../utils/statisticsCharts";
import { useT } from "../components/TextProvider";

function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") ||
    s.getItem("auth:token") ||
    s.getItem("auth:key") ||
    s.getItem("auth:jwt") ||
    s.getItem("auth:auth_token") ||
    null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

const USER_ROLES_ENDPOINT =
  process.env.REACT_APP_USER_ROLES_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/user-roles/";

export default function Statistics() {
  const t = useT();
  const rootRef = useRef(null);
  const [userRole, setUserRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [user, setUser] = useState(() => getStoredUser());

  useEffect(() => {
    let abort = false;

    async function determineRole() {
      if (!user?.id) {
        if (!abort) { setUserRole("student"); setRoleLoading(false); }
        return;
      }
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(USER_ROLES_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        if (!res.ok) {
          if (!abort) { setUserRole("student"); setRoleLoading(false); }
          return;
        }
        const list = await res.json().catch(() => []);
        if (!Array.isArray(list)) {
          if (!abort) { setUserRole("student"); setRoleLoading(false); }
          return;
        }
        const isAdmin = list.some((r) => Number(r?.user) === Number(user.id) && Number(r?.role) === 3);
        if (isAdmin) { if (!abort) { setUserRole("admin"); setRoleLoading(false); } return; }
        const isTrainer = list.some((r) => Number(r?.user) === Number(user.id) && Number(r?.role) === 2);
        if (!abort) { setUserRole(isTrainer ? "trainer" : "student"); setRoleLoading(false); }
      } catch {
        if (!abort) { setUserRole("student"); setRoleLoading(false); }
      }
    }

    determineRole();
    return () => { abort = true; };
  }, [user?.id]);

  useEffect(() => {
    const onStorage = (e) => { if (!e.key || e.key.startsWith("auth:")) setUser(getStoredUser()); };
    const onAuthChanged = () => setUser(getStoredUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:changed", onAuthChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:changed", onAuthChanged);
    };
  }, []);

  useEffect(() => {
    if (!roleLoading && userRole && rootRef.current) {
      try {
        mountStatistics(rootRef.current, userRole);
      } catch {
        setLoadError("Ma'lumotlarni yuklashda xatolik yuz berdi.");
      }
    }
  }, [userRole, roleLoading]);

  const isStudent = userRole === "student";
  const isStaff   = userRole === "trainer" || userRole === "admin";
  const roleLabel  = userRole === "admin" ? "Administrator" : userRole === "trainer" ? "O'qituvchi" : "Talaba";

  /* ── загрузка роли ── */
  if (roleLoading) {
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:"16px", color:"#5d6676" }}>
        <style>{`@keyframes _spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ width:"40px", height:"40px", border:"3px solid #e5e8f0", borderTopColor:"#3a6cf6", borderRadius:"50%", animation:"_spin 0.8s linear infinite" }} />
        <span style={{ fontSize:"14px" }}>Yuklanmoqda...</span>
      </div>
    );
  }

  /* ── ошибка ── */
  if (loadError) {
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:"12px" }}>
        <span style={{ fontSize:"32px" }}>⚠️</span>
        <p style={{ color:"#ef4444", fontWeight:600, margin:0 }}>{loadError}</p>
        <button style={{ padding:"8px 18px", borderRadius:"8px", border:"1px solid #e5e8f0", cursor:"pointer", background:"#fff" }} onClick={() => window.location.reload()}>
          Qayta urinib ko'ring
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="stats-wrap">
      <style>{`
        .stats-wrap{--s-ring:#e5e8f0;--s-muted:#5d6676;--s-accent:#3a6cf6;--s-shadow:0 10px 30px rgba(0,0,0,.08);--s-grid:#e9edf4;}
        .s-wrap{margin:28px auto;padding:32px 46px;}
        .s-header{margin-bottom:28px;}
        .s-header h1{margin:0 0 6px;font-size:22px;font-weight:700;}
        .s-role-badge{display:inline-flex;align-items:center;gap:6px;background:#f0f4ff;border:1px solid #d0dcff;border-radius:999px;padding:3px 12px;font-size:12px;font-weight:600;color:var(--s-accent);margin-bottom:8px;}
        .s-sub{font-size:13px;color:var(--s-muted);margin:0;}
        .dashboard{display:grid;grid-template-columns:1fr;gap:24px;padding:10%}
        .card{position:relative;background:#fff;border:1px solid var(--s-ring);border-radius:16px;box-shadow:var(--s-shadow);padding:18px;display:grid;grid-template-rows:auto 1fr;gap:10px;}
        .card h2{margin:0;font-size:16px;font-weight:600;}
        .sub{color:var(--s-muted);font-size:13px;margin-top:4px;line-height:1.5;}
        .chart{position:relative;width:100%;height:auto;}
        .chart svg{width:100%;height:auto;display:block;}
        .chart svg:empty{background:linear-gradient(90deg,#f0f2f8 25%,#e4e8f5 50%,#f0f2f8 75%);background-size:200% 100%;animation:shimmer 1.4s ease infinite;border-radius:10px;min-height:220px;}
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        .chart-select{margin-top:10px;padding:6px 10px;border-radius:6px;border:1px solid var(--s-ring);font-size:14px;cursor:pointer;width:100%;max-width:280px;background:#fff;}
        .zoom-btn{display:none;}
        .tooltip{position:absolute;pointer-events:none;background:#0e1320;color:#fff;border:1px solid rgba(14,19,32,.3);padding:8px 10px;border-radius:10px;font-size:12px;box-shadow:var(--s-shadow);opacity:0;transform:scale(.98);transition:opacity .15s,transform .15s;white-space:nowrap;z-index:3;}
        .tooltip.show{opacity:1;transform:scale(1);}
        .axis-line{stroke:#d6dbe6;stroke-width:1;}.grid-line{stroke:var(--s-grid);stroke-width:1;}
        .tick text{fill:#3a4050;font-size:14px;dominant-baseline:hanging;}
        .bar-group{transform-origin:bottom;transform-box:fill-box;transition:transform .18s cubic-bezier(.2,.8,.2,1),filter .18s;}
        .bar{rx:6;ry:6;filter:drop-shadow(0 1px 1px rgba(0,0,0,.05));transition:fill .18s;}
        .bar-group,.bar-group.hover{filter:none !important;}
        .bar-group.hover .bar{filter:drop-shadow(0 6px 14px rgba(0,0,0,.10));}
        .bar-label{font-weight:700;font-size:12px;fill:#2b3242;pointer-events:none;transition:transform .18s cubic-bezier(.2,.8,.2,1);}
        .bar-group.hover .bar-label{stroke:none !important;paint-order:fill !important;}
        .bar-label.hover{transform:scale(1.08);}
        .bar-label:after{content:none !important;}
        .area{fill:url(#areaGrad);opacity:.6;}.line{fill:none;stroke:var(--s-accent);stroke-width:2.5;filter:drop-shadow(0 1px 1px rgba(0,0,0,.06));}
        .point{fill:#fff;stroke:var(--s-accent);stroke-width:2;r:4.5;transition:r .12s,stroke-width .12s;}.point.hover{r:6.5;stroke-width:2.5;}
        .data-label{font-size:12px;fill:#2b3242;text-anchor:middle;pointer-events:none;stroke:currentColor;stroke-width:0;paint-order:stroke fill;transition:transform .18s cubic-bezier(.2,.8,.2,1),opacity .18s;}
        .data-label.hover{stroke-width:.7;}.hover-line{stroke:#92a0c6;stroke-width:1;stroke-dasharray:3 4;opacity:0;transition:opacity .15s;}.hover-line.show{opacity:1;}
        .ring{fill:none;stroke:var(--s-grid);stroke-width:10;}
        .slice-group{transition:transform .22s cubic-bezier(.2,.8,.2,1),filter .22s;transform-origin:50% 50%;transform-box:fill-box;}
        .slice{filter:drop-shadow(0 2px 2px rgba(0,0,0,.06));transition:fill .15s;pointer-events:none;}
        .slice.accent{filter:drop-shadow(0 3px 6px rgba(58,108,246,.25)) drop-shadow(0 10px 24px rgba(58,108,246,.18));stroke:rgba(0,0,0,.06);stroke-width:1;}
        .legend{display:grid;gap:10px;}.legend-item{display:grid;grid-template-columns:14px auto max-content;align-items:center;gap:10px;padding:8px 10px;border-radius:10px;background:#f4f6fb;border:1px solid var(--s-ring);}
        .swatch{width:14px;height:14px;border-radius:3px;border:1px solid rgba(0,0,0,.1);}.legend-title{font-weight:600;letter-spacing:.2px;}.legend-val{color:var(--s-muted);font-variant-numeric:tabular-nums;}
        @media(max-width:980px){.dashboard{grid-template-columns:1fr;padding:3%}.s-wrap{padding:20px 16px;}}
        .modal{position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(9,12,20,.55);z-index:1000;}
        .modal.show{display:flex;}
        .modal-content{background:#fff;border-radius:16px;border:1px solid var(--s-ring);box-shadow:0 20px 60px rgba(0,0,0,.25);width:90%;height:90vh;max-height:90vh;display:grid;grid-template-rows:auto 1fr;overflow:hidden;}
        .modal-head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid var(--s-ring);}
        .modal-title{font-weight:600;}.modal-close{background:#fff;border:1px solid var(--s-ring);border-radius:10px;padding:6px 10px;cursor:pointer;}
        .modal-body{padding:10px 14px;overflow:auto;}
        .chart.flex-center{display:flex;align-items:center;justify-content:center;width:100%;height:100%;}
        .chart.flex-center svg{display:block;max-width:100%;max-height:100%;}
        .chart.flex-center[data-aspect="960/460"] svg{width:100%;height:auto;aspect-ratio:960/460;}
        .pie-modal-card{border:none;box-shadow:none;padding:6px;min-height:auto;}
        .pie-layout{display:flex;justify-content:start;align-items:center;height:calc(86vh - 80px);}
        .pie-chart{display:flex;align-items:center;justify-content:center;width:60%;height:60vh;}
        .pie-canvas{width:100%;max-width:100%;height:100%;max-height:100%;aspect-ratio:1/1;}
        .pie-canvas svg{width:100%;height:100%;}.pie-legend{width:30%;max-height:100%;overflow:auto;}
        @keyframes slideInLeader{0%{opacity:0;transform:translateX(-20px) translateY(10px);}100%{opacity:1;transform:translateX(0) translateY(0);}}
        @keyframes medalBounce{0%{transform:scale(0) rotate(-180deg);opacity:0;}50%{transform:scale(1.2) rotate(20deg);}100%{transform:scale(1) rotate(0);opacity:1;}}
        @keyframes scoreScale{0%{transform:scale(.5);opacity:0;}100%{transform:scale(1);opacity:1;}}
      `}</style>

      <div className="s-wrap">
        <header className="s-header">
          <div className="s-role-badge">● {roleLabel}</div>
          <h1>{t("Statistika")}</h1>
          <p className="s-sub">
            {isStudent
              ? "Nazorat ishlari natijalari va fanlar bo'yicha o'z natijangizni kuzating"
              : "Talabalar ko'rsatkichlari va fanlar bo'yicha guruh tahlili"}
          </p>
        </header>

        <div className="dashboard">
          {isStudent && (
            <section className="card" id="marks-card">
              <div>
                <h2>{t('Gis5Title') || 'Nazorat ishlari bahosi'}</h2>
                <div className="sub">Barcha fanlar bo'yicha nazorat ishlari baholaringiz</div>
              </div>
              <div className="chart">
                <svg viewBox="0 0 960 460" preserveAspectRatio="xMidYMid meet"></svg>
                <div className="tooltip" aria-hidden="true"></div>
              </div>
              <div className="legend"></div>
            </section>
          )}

          {isStudent && (
            <section className="card" id="single-course-card">
              <div>
                <h2>{t('Gis6Title') || "Fan bo'yicha batafsil"}</h2>
                <div className="sub">Tanlangan fan bo'yicha har bir nazorat ishi natijasi</div>
                <select id="course-selector" className="chart-select">
                  <option value="">{t('Fanni tanlang')}</option>
                </select>
              </div>
              <div className="chart">
                <svg viewBox="0 0 960 460" preserveAspectRatio="xMidYMid meet"></svg>
                <div className="tooltip" aria-hidden="true"></div>
              </div>
              <div className="legend"></div>
            </section>
          )}

          {isStaff && (
            <section className="card" id="students-avg-card">
              <div>
                <h2>{t('Gis7Title') || "Talabalar o'rtacha bahosi"}</h2>
                <div className="sub">Tanlangan fan bo'yicha har bir talabaning o'rtacha natijasi</div>
                <select id="avg-course-selector" className="chart-select">
                  <option value="">— Fanni tanlang —</option>
                </select>
              </div>
              <div className="chart">
                <svg viewBox="0 0 960 460" preserveAspectRatio="xMidYMid meet"></svg>
                <div className="tooltip" aria-hidden="true"></div>
              </div>
              <div className="legend"></div>
            </section>
          )}

          {isStaff && (
            <section className="card" id="subjects-avg-card">
              <div>
                <h2>{t('Gis8Title') || "Fanlar bo'yicha o'rtacha baho"}</h2>
                <div className="sub">Qaysi fanlar guruhga qiyin yoki oson ekanini ko'rsatadi</div>
              </div>
              <div className="chart">
                <svg viewBox="0 0 960 460" preserveAspectRatio="xMidYMid meet"></svg>
                <div className="tooltip" aria-hidden="true"></div>
              </div>
              <div className="legend"></div>
            </section>
          )}
        </div>
      </div>

      <div className="modal" id="chart-modal" aria-hidden="true">
        <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div className="modal-head">
            <div className="modal-title" id="modal-title">Diagramma</div>
            <button className="modal-close" aria-label="Yopish">{t('Yopish')}</button>
          </div>
          <div className="modal-body"></div>
        </div>
      </div>
    </div>
  );
}
